import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../api';
import { useCart } from '../context/CartContext';
import {
  Package, Truck, Clock, CheckCircle2, XCircle, LogOut, ShoppingBag, RefreshCw, ExternalLink, RotateCcw
} from 'lucide-react';

const STATUS_META = {
  pending:    { label: 'Pending',    color: '#F4A300', icon: Clock },
  confirmed:  { label: 'Confirmed',  color: '#2A9D8F', icon: CheckCircle2 },
  dispatched: { label: 'Dispatched', color: '#4A90E2', icon: Truck },
  delivered:  { label: 'Delivered',  color: '#2A9D8F', icon: Package },
  cancelled:  { label: 'Cancelled',  color: '#E63946', icon: XCircle },
};

const AccountPage = () => {
  const { user, loading, logout, login } = useAuth();
  const { add, setOpen } = useCart();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);

  const load = async () => {
    setOrdersLoading(true);
    try {
      const rows = await authApi.myOrders();
      setOrders(rows);
    } catch {
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) return;
    load();
  }, [loading, user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF4E6] flex items-center justify-center">
        <div className="w-12 h-12 rounded-full border-4 border-[#E63946] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="bg-[#FFF4E6] min-h-screen">
        <Navbar />
        <section className="px-5 md:px-8 pt-32 pb-16">
          <div className="max-w-md mx-auto text-center bg-white rounded-3xl crunch-shadow p-8">
            <div className="text-xs font-bold uppercase tracking-widest text-[#E63946]">My Account</div>
            <h1 className="font-display text-4xl md:text-5xl uppercase leading-[0.9] mt-2">Sign in to see your orders.</h1>
            <p className="font-brico mt-3 opacity-70">Sign in with Google. We'll pull in any past orders you placed with the same email.</p>
            <button onClick={login} className="btn-chunky primary mt-6 w-full justify-center">
              <svg viewBox="0 0 48 48" width="18" height="18"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.9 32.4 29.4 35.5 24 35.5c-6.4 0-11.5-5.2-11.5-11.5S17.6 12.5 24 12.5c2.9 0 5.6 1.1 7.7 3l5.7-5.7C33.6 6.4 29 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5c11 0 19.5-8.9 19.5-19.5 0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 19 12.5 24 12.5c2.9 0 5.6 1.1 7.7 3l5.7-5.7C33.6 6.4 29 4.5 24 4.5 16.4 4.5 9.8 8.7 6.3 14.7z"/><path fill="#4CAF50" d="M24 43.5c5.3 0 10.1-2 13.6-5.3l-6.3-5.3c-1.9 1.4-4.5 2.3-7.4 2.3-5.3 0-9.8-3.1-11.3-7.4l-6.5 5c3.4 6.3 10 10.7 17.9 10.7z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.7 2-2.1 3.8-4 5.1l6.3 5.3c-.4.4 6.8-4.9 6.8-14.4 0-1.2-.1-2.3-.4-3.5z"/></svg>
              Sign in with Google
            </button>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  const reorder = (order) => {
    (order.items || []).forEach((it) => {
      // Cart uses product_id, title, price, qty, image, color; use fallback image/color from product
      add({
        id: it.product_id,
        title: it.title,
        price: it.price,
        image: it.image || 'https://images.unsplash.com/photo-1613919113640-25732ec5e61f?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
        color: it.color || '#E63946',
      });
    });
    setOpen(true);
  };

  return (
    <div className="bg-[#FFF4E6] min-h-screen">
      <Navbar />
      <section className="px-5 md:px-8 pt-28 pb-16">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-3xl crunch-shadow p-5 md:p-6 flex items-center gap-4">
            {user.picture ? (
              <img src={user.picture} alt={user.name} className="w-14 h-14 rounded-full border-2 border-[#1a1a1a]" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-[#E63946] text-white font-display flex items-center justify-center text-xl">
                {(user.name || user.email || '?').charAt(0).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold uppercase tracking-widest opacity-70">Signed in</div>
              <div className="font-display text-2xl truncate">{user.name || 'Cruncher'}</div>
              <div className="text-sm font-brico opacity-70 truncate">{user.email}</div>
            </div>
            <button onClick={logout} className="btn-chunky outline text-xs"><LogOut size={14} /> Logout</button>
          </div>

          <div className="mt-8 flex items-center justify-between">
            <h2 className="font-display text-4xl md:text-5xl uppercase">Past Orders</h2>
            <button onClick={load} className="p-2 rounded-full bg-white crunch-shadow-sm"><RefreshCw size={16} /></button>
          </div>

          {ordersLoading ? (
            <div className="mt-6 text-center font-brico opacity-70">Loading…</div>
          ) : orders.length === 0 ? (
            <div className="mt-6 bg-white rounded-3xl crunch-shadow p-8 text-center">
              <ShoppingBag size={40} className="mx-auto text-[#E63946]" />
              <div className="font-display text-3xl uppercase mt-3">No orders yet.</div>
              <p className="font-brico opacity-70 mt-2">Any order you place with this email will show up here.</p>
              <Link to="/shop" className="btn-chunky primary mt-5 inline-flex">Browse Snacks</Link>
            </div>
          ) : (
            <div className="mt-6 grid gap-4">
              {orders.map((o) => {
                const meta = STATUS_META[o.status] || STATUS_META.pending;
                const StatusIcon = meta.icon;
                return (
                  <div key={o.id} className="bg-white rounded-3xl crunch-shadow p-5 md:p-6">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="text-xs font-bold uppercase tracking-widest opacity-70">Order</div>
                        <div className="font-mono text-sm break-all">{o.id}</div>
                        <div className="text-xs font-brico opacity-60 mt-1">{new Date(o.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</div>
                      </div>
                      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-white font-brico font-bold text-xs uppercase tracking-widest" style={{ background: meta.color }}>
                        <StatusIcon size={14} /> {meta.label}
                      </div>
                    </div>

                    <div className="mt-4 grid md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        {o.items.map((i) => (
                          <div key={`${o.id}-${i.product_id}`} className="flex items-center justify-between font-brico text-sm">
                            <span>{i.title} ×{i.qty}</span>
                            <span className="font-bold">₹{i.price * i.qty}</span>
                          </div>
                        ))}
                      </div>
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center justify-between font-brico"><span className="opacity-70">Subtotal</span><span>₹{o.subtotal}</span></div>
                        {o.discount > 0 && <div className="flex items-center justify-between font-brico text-[#2A9D8F]"><span>Discount ({o.coupon_code})</span><span>−₹{o.discount}</span></div>}
                        <div className="flex items-center justify-between font-brico"><span className="opacity-70">Shipping</span><span>{o.shipping === 0 ? 'FREE' : `₹${o.shipping}`}</span></div>
                        <div className="flex items-center justify-between font-display text-xl uppercase pt-2 border-t border-black/10"><span>Total</span><span>₹{o.total}</span></div>
                      </div>
                    </div>

                    {o.tracking_id && (
                      <div className="mt-4 bg-[#FFF4E6] rounded-2xl p-3 border-2 border-dashed border-[#1a1a1a]/20 flex items-center justify-between flex-wrap gap-2">
                        <div>
                          <div className="text-xs font-bold uppercase tracking-widest opacity-70 flex items-center gap-2"><Truck size={12} /> Courier</div>
                          <div className="font-display">{o.courier}</div>
                          <div className="font-mono text-xs">{o.tracking_id}</div>
                        </div>
                      </div>
                    )}

                    <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
                      <Link to={`/track?id=${o.id}&email=${encodeURIComponent(user.email)}`} className="btn-chunky outline text-xs">
                        <ExternalLink size={14} /> Full details
                      </Link>
                      <button onClick={() => reorder(o)} className="btn-chunky primary text-xs">
                        <RotateCcw size={14} /> Reorder
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default AccountPage;
